import React, { useState, Fragment } from 'react';
import { EditIcon, CheckIcon, XIcon, ChevronDownIcon, ChevronUpIcon, PackageIcon } from 'lucide-react';
import { CatalogSchema, CatalogProduct, CatalogField } from '../CatalogsManagement';
interface ProductTableProps {
  products: CatalogProduct[];
  schema: CatalogSchema | null;
  onEdit: (product: CatalogProduct) => void;
  onToggleStatus: (productId: string) => void;
  locale: string;
}
export function ProductTable({
  products,
  schema,
  onEdit,
  onToggleStatus,
  locale
}: ProductTableProps) {
  // State for expanded rows
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  // Format currency
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(amount);
  };
  // Toggle row expansion
  const toggleRowExpand = (productId: string) => {
    setExpandedRows(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };
  // Format custom field value based on its type
  const formatCustomFieldValue = (field: CatalogField, value: any) => {
    if (value === undefined || value === null) return '-';
    switch (field.dataType) {
      case 'boolean':
        return value ? 'Yes' : 'No';
      case 'date':
        return new Date(value).toLocaleDateString(locale);
      case 'currency':
        return typeof value === 'object' && value.amount && value.currency ? formatCurrency(value.amount, value.currency) : value;
      default:
        return value.toString();
    }
  };
  return <div className="bg-white shadow overflow-hidden border-b border-gray-200 sm:rounded-lg" data-id="element-13918">
      {products.length === 0 ? <div className="text-center py-12" data-id="element-13919">
          <PackageIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-13920" />
          <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-13921">
            No products found
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-13922">
            Get started by adding a new product or adjusting your filters.
          </p>
        </div> : <table className="min-w-full divide-y divide-gray-200" data-id="element-13923">
          <thead className="bg-gray-50" data-id="element-13924">
            <tr data-id="element-13925">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13926">
                Product
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13927">
                Vendor SKU
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13928">
                Price
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13929">
                Stock
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13930">
                Manufacturer
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13931">
                Category
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13932">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13933">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-13934">
            {products.map(product => <Fragment key={product.id} data-id="element-13935">
                <tr className={expandedRows[product.id] ? 'bg-gray-50' : 'hover:bg-gray-50'} data-id="element-13936">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-13937">
                    <div className="flex items-center" data-id="element-13938">
                      <button onClick={() => toggleRowExpand(product.id)} className="mr-2 text-gray-400 hover:text-gray-500" title={expandedRows[product.id] ? 'Collapse' : 'Expand'} data-id="element-13939">
                        {expandedRows[product.id] ? <ChevronUpIcon className="h-4 w-4" data-id="element-13940" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-13941" />}
                      </button>
                      <div className="text-sm font-medium text-indigo-600" data-id="element-13942">
                        {product.name}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-13943">
                    {product.vendorSku}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-13944">
                    {formatCurrency(product.price, product.currency)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-13945">
                    {product.availableStock > 0 ? <span className="text-green-600" data-id="element-13946">
                        {product.availableStock}
                      </span> : <span className="text-red-600" data-id="element-13947">Out of stock</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-13948">
                    {product.manufacturer}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-13949">
                    {product.category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-13950">
                    {product.isActive ? <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800" data-id="element-13951">
                        <CheckIcon className="h-4 w-4 mr-1" data-id="element-13952" />
                        Active
                      </span> : <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800" data-id="element-13953">
                        <XIcon className="h-4 w-4 mr-1" data-id="element-13954" />
                        Inactive
                      </span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-13955">
                    <div className="flex justify-end space-x-2" data-id="element-13956">
                      <button onClick={() => onEdit(product)} className="text-indigo-600 hover:text-indigo-900" title="Edit Product" data-id="element-13957">
                        <EditIcon className="h-4 w-4" data-id="element-13958" />
                      </button>
                      <button onClick={() => onToggleStatus(product.id)} className={product.isActive ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'} title={product.isActive ? 'Deactivate' : 'Activate'} data-id="element-13959">
                        {product.isActive ? <XIcon className="h-4 w-4" data-id="element-13960" /> : <CheckIcon className="h-4 w-4" data-id="element-13961" />}
                      </button>
                    </div>
                  </td>
                </tr>
                {/* Expanded row with custom attributes and additional details */}
                {expandedRows[product.id] && <tr data-id="element-13962">
                    <td colSpan={8} className="px-6 py-4 bg-gray-50 border-b border-gray-200" data-id="element-13963">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-13964">
                        <div data-id="element-13965">
                          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-13966">
                            Product Details
                          </h4>
                          <dl className="grid grid-cols-2 gap-x-4 gap-y-2" data-id="element-13967">
                            {product.hsCode && <>
                                <dt className="text-sm font-medium text-gray-500" data-id="element-13968">
                                  HS Code
                                </dt>
                                <dd className="text-sm text-gray-900" data-id="element-13969">
                                  {product.hsCode}
                                </dd>
                              </>}
                            {product.warranty && <>
                                <dt className="text-sm font-medium text-gray-500" data-id="element-13970">
                                  Warranty
                                </dt>
                                <dd className="text-sm text-gray-900" data-id="element-13971">
                                  {product.warranty}
                                </dd>
                              </>}
                            <dt className="text-sm font-medium text-gray-500" data-id="element-13972">
                              Active From
                            </dt>
                            <dd className="text-sm text-gray-900" data-id="element-13973">
                              {new Date(product.activeFrom).toLocaleDateString(locale)}
                            </dd>
                            {product.activeTo && <>
                                <dt className="text-sm font-medium text-gray-500" data-id="element-13974">
                                  Active To
                                </dt>
                                <dd className="text-sm text-gray-900" data-id="element-13975">
                                  {new Date(product.activeTo).toLocaleDateString(locale)}
                                </dd>
                              </>}
                          </dl>
                        </div>
                        <div data-id="element-13976">
                          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-13977">
                            Packaging
                          </h4>
                          <dl className="grid grid-cols-2 gap-x-4 gap-y-2" data-id="element-13978">
                            {product.outerPackQty && <>
                                <dt className="text-sm font-medium text-gray-500" data-id="element-13979">
                                  Outer Pack Qty
                                </dt>
                                <dd className="text-sm text-gray-900" data-id="element-13980">
                                  {product.outerPackQty} {product.outerPackUnit}
                                </dd>
                              </>}
                            {product.innerPackQty && <>
                                <dt className="text-sm font-medium text-gray-500" data-id="element-13981">
                                  Inner Pack Qty
                                </dt>
                                <dd className="text-sm text-gray-900" data-id="element-13982">
                                  {product.innerPackQty} {product.innerPackUnit}
                                </dd>
                              </>}
                          </dl>
                        </div>
                        <div data-id="element-13983">
                          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-13984">
                            Inventory
                          </h4>
                          <dl className="grid grid-cols-2 gap-x-4 gap-y-2" data-id="element-13985">
                            <dt className="text-sm font-medium text-gray-500" data-id="element-13986">
                              Available
                            </dt>
                            <dd className="text-sm text-gray-900" data-id="element-13987">
                              {product.availableStock}
                            </dd>
                            {product.backordered !== undefined && <>
                                <dt className="text-sm font-medium text-gray-500" data-id="element-13988">
                                  Backordered
                                </dt>
                                <dd className="text-sm text-gray-900" data-id="element-13989">
                                  {product.backordered}
                                </dd>
                              </>}
                            {product.qtyOrdered !== undefined && <>
                                <dt className="text-sm font-medium text-gray-500" data-id="element-13990">
                                  Ordered
                                </dt>
                                <dd className="text-sm text-gray-900" data-id="element-13991">
                                  {product.qtyOrdered}
                                </dd>
                              </>}
                            {product.qtyInvoiced !== undefined && <>
                                <dt className="text-sm font-medium text-gray-500" data-id="element-13992">
                                  Invoiced
                                </dt>
                                <dd className="text-sm text-gray-900" data-id="element-13993">
                                  {product.qtyInvoiced}
                                </dd>
                              </>}
                          </dl>
                        </div>
                      </div>
                      {/* Custom attributes */}
                      {schema && schema.fields.length > 0 && Object.keys(product.customAttributes).length > 0 && <div className="mt-4" data-id="element-13994">
                            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-13995">
                              Custom Attributes
                            </h4>
                            <dl className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2" data-id="element-13996">
                              {schema.fields.map(field => <Fragment key={field.id} data-id="element-13997">
                                  <dt className="text-sm font-medium text-gray-500" data-id="element-13998">
                                    {field.name}
                                  </dt>
                                  <dd className="text-sm text-gray-900" data-id="element-13999">
                                    {formatCustomFieldValue(field, product.customAttributes[field.key])}
                                  </dd>
                                </Fragment>)}
                            </dl>
                          </div>}
                    </td>
                  </tr>}
              </Fragment>)}
          </tbody>
        </table>}
    </div>;
}